/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICacheItem, ICache, ICacheKey } from '@msdyn365-commerce/cache-internal';
import {
    IDictionary,
    IParsedQSP,
    IRenderingHelper,
    IRequestContext,
    msdyn365Commerce,
    SDK_FRAGMENT_NAME,
    SDK_FRAGMENT_NAME_REGEX,
    checkVersionForFeature,
    IPlatformSettings,
    IRenderingContext
} from '@msdyn365-commerce/core-internal';
import { EXCEPTION_EXPECTED501, IInternalTelemetry, StaticTelemetry } from '@msdyn365-commerce/telemetry-internal';
import { NextFunction, Request, Response } from 'express';
import * as fs from 'fs';
import { getCurrency } from 'locale-currency';
import { escape } from 'lodash';
import * as path from 'path';
import { ReactElement } from 'react';
import { renderToString } from 'react-dom/server';
import { Stream } from 'stream';
import { IRefreshConfigs, REFRESH_ACTIONS_CACHE_KEY } from '@msdyn365-commerce/action-internal';
import { toJS } from 'mobx';
import { THEME_MODULE } from '../_server/Definition/moduleDefinition';
import { HttpException } from '../_server/error';
import { IMSDyn365CommerceExtension } from '../app-initialization/models';

export const MODULE_ERROR_TYPE_NAME_FOR_CACHE: string = '__ME';
export const EMPTY_MODULE_RESULT_VALUE = 'empty-module-result';
export const UNREGISTERED_MODULE_VALUE = 'unregistered-module';

// Used to deprecate older modules starting SSK V2
export const SSK2_VERSION = '2.0.0';

/**
 * Creates and returns an ICacheKey object for storing
 * results of module render errors inside the RequestCache
 *
 * @param id The id of the module that encountered an error
 */
export const createCacheKeyForModuleError = (id: string): ICacheKey => {
    return { typeName: MODULE_ERROR_TYPE_NAME_FOR_CACHE, key: id };
};

/**
 * Creates and returns an ICacheItem object that inidcates
 * a module render result was null or empty
 */
export const createCacheValueForEmptyModule = (): ICacheItem<string> => {
    return { item: EMPTY_MODULE_RESULT_VALUE };
};

/**
 * Creates and returns an ICacheItem object that inidcates
 * a module was unregistered and thus not rendered
 */
export const createCacheValueForUnregisteredModule = (): ICacheItem<string> => {
    return { item: UNREGISTERED_MODULE_VALUE };
};

/**
 * Utility function to async check if file exists
 * @param filePath file path to check
 */
export const fileExists = async (filePath: string, telemetry: IInternalTelemetry) => {
    return new Promise((resolve: (pathToFileThatExists?: string) => void, reject: () => void) => {
        if (!filePath) {
            resolve(undefined);
        }

        fs.access(filePath, fs.constants.R_OK, (err: NodeJS.ErrnoException | null) => {
            if (err) {
                resolve(undefined);
            }
            resolve(filePath);
        });
    });
};

/**
 * Utility function to access the site builder rendering helper
 */
export const getRenderingHelper = (): IRenderingHelper | undefined => {
    return (<any>window)._msdyn365.authoringHelper && (<any>window)._msdyn365.authoringHelper.renderingHelper;
};

/**
 * Converts a readable stream (that has yet to emit 'data') to a string
 *
 * @param stream Stream-like object
 */
export const streamToString = (stream: Stream): Promise<string> => {
    return new Promise((resolve: (streamAsString: string) => void) => {
        const stringParts: string[] = [];
        stream.on('data', (buffer: Buffer) => {
            stringParts.push(buffer.toString());
        });
        stream.on('end', () => {
            resolve(stringParts.join(''));
        });
    });
};

/**
 * Checks is value is truthy
 * @param value value to check
 */
export const isTruthy = (value: number | boolean | string | undefined | null) => {
    if (value === undefined || value === null) {
        return false;
    }
    switch (String(value).toLocaleLowerCase()) {
        case '1':
        case 'true':
        case 'yes':
            return true;
        case '0':
        case 'false':
        case 'no':
        case 'undefined':
        case '':
            return false;
        default:
            return !!value;
    }
};

/**
 * Checks optional QSP value and returns as a structured type
 *
 * @param qspValue QSP value to check
 */
export const parseToQSPObject = <TValue>(qspValue: TValue): IParsedQSP<TValue> => {
    return {
        hasValue: qspValue !== undefined,
        isTruthy: isTruthy(String(qspValue)),
        value: qspValue
    };
};

/**
 * Server has allowed list of QSPs, so QSPs going through Server have be put beind a allowed list of QSP such as item.
 *
 * Example:
 *  In localhost, we'll have `?debug=true` but when running through Server, this is equivalent to ?item=debug:true
 *
 * This method also supports parsing out a comma separated item list, makes assumption that everything is properly URIEncoded
 *  e.g. ?item=debug:true,foo:bar,bar:123
 *
 * @param itemQueryString the `item` query string value
 */
export const parseItemQSP = (itemQueryString: string | undefined | null): IDictionary<string> => {
    const result: IDictionary<string> = {};
    if (itemQueryString === undefined || itemQueryString === null) {
        return result;
    }
    const decodedUriParts = decodeURIComponent(itemQueryString).split(',');
    decodedUriParts.forEach((qsp: string) => {
        const qspParts = qsp.split(':');
        if (qspParts && qspParts.length === 2) {
            const [key, value] = qspParts;
            if (key.length && value.length) {
                result[key] = value;
            }
        }
    });
    return result;
};

export const LinkTag = (baseUrl: string, preloadAttribute: string, staticFlushQuery: string) => (src: string) =>
    `<link rel="stylesheet" href="${baseUrl}${src.concat(staticFlushQuery)}" ${preloadAttribute} crossOrigin="anonymous" />`;

export const CommentTag = (comment: string | object) => {
    if (typeof comment === 'object') {
        return Object.keys(comment)
            .map(key => `<!-- key: ${escape(key)}, value: ${escape(JSON.stringify(comment[key]))} -->`)
            .join('');
    }
    return `<!-- ${escape(comment)} -->`;
};

/**
 * Wraps express routes and makes sure to catch & rethrow any errors.
 * Actual handling will be done by the handlerServerError method that logs error and sends response
 * @param fn Function for wrapped route
 */
export const safeRoute = fn => (...args: any[]) =>
    fn(...args).catch((error: Error) => {
        args[2] && args[2](error);
    });

/**
 * Sample helper
 * @param res Response
 */
export const throwHelper = (req: Request, res: Response, next: NextFunction, message: string) => {
    throw new HttpException(501, StaticTelemetry.stringFormat(EXCEPTION_EXPECTED501, [message]));
};

export const getAbsoluteUri = (req): string => {
    return `${req.protocol}://${req.get('host')}${req.originalUrl}`;
};

/**
 * Wrapped render to string method that replaces the SDK Fragment element with nothing
 * used as a drop-in for renderToString on elements where we blindly set innerHTML.
 *
 * @param element the element to render
 */
export const patchedRenderToString = (element: ReactElement, disableServerSideErrorChecking: boolean) => {
    const renderResult = renderToString(element);
    if (disableServerSideErrorChecking && !renderResult.startsWith(`<${SDK_FRAGMENT_NAME}`)) {
        return renderResult;
    }
    return renderResult.replace(SDK_FRAGMENT_NAME_REGEX, '');
};

/**
 * Checks if client side hydration should happen before loadData
 * @param requestContext Request context object
 */
export const shouldOptimizeHydrate = (requestContext: IRequestContext) => {
    const clientRenderOptimizationEnabled = requestContext.features && requestContext.features.optimize_client_hydrate;
    const clientRenderQsp = requestContext.query && requestContext.query.optimize_hydrate === 'true';
    return (clientRenderOptimizationEnabled || clientRenderQsp) && checkVersionForFeature('optimize_client_hydrate');
};

export const CurrencyCodeIdentifier = 'cc';
export const getCurrencyCodeFromContext = (requestContext: IRequestContext) => {
    return (
        (requestContext && requestContext.query && requestContext.query[CurrencyCodeIdentifier]) ||
        (requestContext.cookies && requestContext.cookies.get<string>(CurrencyCodeIdentifier).value) ||
        (requestContext.channel && requestContext.channel.Currency) ||
        getCurrency(requestContext.locale)
    );
};

/**
 * Extracts the name of the module from the path to its definition file
 * @param definitionPath path to the definition file
 */
export const getModuleName = (definitionPath: string) => {
    if (!definitionPath || definitionPath === '') {
        return '';
    }

    return path.basename(definitionPath, THEME_MODULE.DEFINITION_FILE);
};

/**
 * Returns the binder object for the given module
 * @param typeName name of the module
 */
export const getModuleBinder = (typeName: string) => (<IMSDyn365CommerceExtension>(<unknown>msdyn365Commerce)).moduleBinder(typeName);

/**
 * Returns name of the parent module for given module name
 * @param moduleName name of the module
 */
export const getParentModuleName = (moduleName: string) => {
    const moduleBinder = getModuleBinder(moduleName);

    if (!moduleBinder) {
        return '';
    }

    return getModuleName(moduleBinder.parentDefinitionPath);
};

/**
 * helper to read actions to be refreshed from request cache.
 * @param requestCache request cache where refresh actions are stored
 * @returns list of refresh action configs
 */
export const getAllActionsToBeRefreshed = (requestCache: ICache) => {
    let allRefreshActions: IRefreshConfigs[] = [];
    const refreshActionsFromCache = requestCache.get<IRefreshConfigs[]>(REFRESH_ACTIONS_CACHE_KEY);
    if (refreshActionsFromCache) {
        allRefreshActions = toJS(refreshActionsFromCache.item);
    }

    return allRefreshActions;
};

export const isLazyRenderModuleEnabled = (requestContext: IRequestContext | IRenderingContext): boolean => {
    if ((<IRequestContext>requestContext)?.params?.isEditor || isTruthy((<IRenderingContext>requestContext)?.query?.editorial)) {
        return false;
    }

    if (requestContext?.query?.disable_lazy_render_support) {
        return false;
    }

    return (
        isModuleEntryPointsEnabled(msdyn365Commerce.platformSettings || <IPlatformSettings>{}) &&
        requestContext?.features?.disable_lazy_render_support !== true
    );
};

export const isModuleEntryPointsEnabled = (platformSettings: IPlatformSettings): boolean => {
    return platformSettings.enableModuleEntryPoints === true;
};

/**
 * Helper function which utilizes time slicing Settimeout method in a promise which helps in breaking long running JS task on the main thread.
 * Use with caution: a setTimeout promise creates a microtask which the event loop prioritizes execution which can again block the main thread more and increase LCP / TBT.
 *
 * @param requestContext
 */
export const timeSliceLongJSTask = async (requestContext: IRequestContext) => {
    if (requestContext?.features?.loadData_optimization) {
        await new Promise(resolve => setTimeout(resolve, 0));
    }
};
